import axios, { AxiosError, AxiosResponse } from "axios";
import { EndpointRequest } from "../../models/endpoints/endpoints.model";
import { getCookieToken } from "../session/auth.service";
import { getAuth0 } from "../auth0/auth0.service";

export default class HttpService {
	// Endpoints URL
	private domain: string | null = null;

	// Axios client
	private axiosClient = axios.create({
		timeout: 900000000,
	});

	// Public HTTP methods
	get = (url: string, request?: EndpointRequest): Promise<never> =>
		this.setRequest("get", this.setUrl(this.domain!, url), request);
	post = (url: string, request: EndpointRequest): Promise<never> =>
		this.setRequest("post", this.setUrl(this.domain!, url), request);
	put = (url: string, request: EndpointRequest): Promise<never> =>
		this.setRequest("put", this.setUrl(this.domain!, url), request);
	delete = (url: string, request?: EndpointRequest): Promise<never> =>
		this.setRequest("delete", this.setUrl(this.domain!, url), request);
	patch = (url: string, request?: EndpointRequest): Promise<never> =>
		this.setRequest("patch", this.setUrl(this.domain!, url), request);

	constructor(domain: string) {
		this.domain = domain;
	}

	private setUrl = (domain: string, endpointUrl: string) => `${domain}${endpointUrl}`;

	private setRequest = async (
		method: string,
		url: string,
		request?: EndpointRequest
	): Promise<never> => {
		// Add token: Authorization
		const token: string | undefined = getCookieToken();
		if (token) {
			this.axiosClient.defaults.headers.common["Authorization"] = `Bearer ${token}`;
		}

		let result: Promise<never>;

		switch (method) {
			case "get":
				result = this.axiosClient.get(url, request?.config);
				break;
			case "post":
				result = this.axiosClient.post(url, request?.body, request?.config);
				break;
			case "put":
				result = this.axiosClient.put(url, request?.body, request?.config);
				break;
			case "delete":
				result = this.axiosClient.delete(url, request?.config);
				break;
			case "patch":
				result = this.axiosClient.patch(url, request?.body, request?.config);
				break;
			default:
				console.error("HttpService: No valid method found");
				result = this.axiosClient.get(url, request?.config);
				break;
		}

		// Handle success/error
		result = result
			.then((response: AxiosResponse<never>) => {
				return response.data;
			})
			.catch((error: AxiosError) => {
				if (error.response?.status === 401) {
					console.log(
						"Unauthenticated error: " +
						error.response?.status +
						" - " +
						error.response?.statusText
					);
					const auth0 = getAuth0();

					if (auth0) {
						console.log("Redirecting to login page due that the session has expired");
						auth0.logout({
							logoutParams: {
								returnTo:
									window.location.origin + "/login?errorType=session_expired",
							},
						});
					}

					// by returning a promise that neither resolves nor rejects, the request will be in a pending state until the user is redirected
					// to the login page
					return new Promise(() => { });
				}

				return Promise.reject(error);
			});

		return result;
	};
}
